(function() {
  // Prevent multiple executions
  if (customElements.get('scroll-shadow')) {
    return;
  }

  const template = `
    <style>
    :host {
      display: inline-block;
      position: relative;
    }
    :host([hidden]) {
      display: none;
    }
    :host([data-nav]) {
      padding: 0 50px;
    }
    :host([data-nav]) ::slotted(*:not([data-nav-prev]):not([data-nav-next])) {
      padding: 0 50px;
    }
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      z-index: 5;
      border-radius: var(--scroll-shadow-radius);
      background:
        var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0))) top/100% var(--top),
        var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0))) bottom/100% var(--bottom),
        var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(0,0,0,.2), rgba(0,0,0,0))) left/var(--left) 100%,
        var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(0,0,0,.2), rgba(0,0,0,0))) right/var(--right) 100%;
      background-repeat: no-repeat;
    }
    .nav-button {
      position: absolute;
      top: var(--nav-button-top, 50%);
      transform: translateY(-50%);
      z-index: 10;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(0, 0, 0, 0.1);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    .nav-button svg {
      -webkit-transition: all 300ms ease;
      -moz-transition: all 300ms ease;
      -ms-transition: all 300ms ease;
      -o-transition: all 300ms ease;
      transition: all 300ms ease;
    }
    .nav-button:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    .nav-button:hover svg {
      transform: scale(1.2);
    }
    .nav-button:active {
      transform: translateY(-50%) scale(0.95);
    }
    .nav-button.disabled {
      opacity: 0.3;
      display: none;
      pointer-events: none;
    }
    .nav-button.prev {
      left: var(--nav-button-offset, 0);
    }
    .nav-button.next {
      right: var(--nav-button-offset, 0);
    }
    .nav-button.has-svg::before {
      display: none;
    }
    .nav-button::before {
      content: '';
      width: 10px;
      height: 10px;
      border-top: 2px solid #333;
      border-right: 2px solid #333;
    }
    .nav-button.prev::before {
      transform: rotate(-135deg);
      margin-left: 3px;
    }
    .nav-button.next::before {
      transform: rotate(45deg);
      margin-right: 3px;
    }
    ::slotted([slot="nav-prev-icon"]),
    ::slotted([slot="nav-next-icon"]) {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    ::slotted([slot="nav-prev-icon"]) svg,
    ::slotted([slot="nav-next-icon"]) svg {
      width: 100%;
      height: 100%;
    }
    .nav-buttons-container {
      display: none;
    }
    :host([data-nav]) .nav-buttons-container {
      display: block;
    }
    </style>
    <slot></slot>
    <s></s>
    <div class="nav-buttons-container">
      <button class="nav-button prev" aria-label="Previous">
        <slot name="nav-prev-icon"></slot>
      </button>
      <button class="nav-button next" aria-label="Next">
        <slot name="nav-next-icon"></slot>
      </button>
    </div>
  `

  const updaters = new WeakMap()

  class ScrollShadowElement extends HTMLElement {
  static get observedAttributes() {
    return ['el', 'data-nav']
  }

  get el() {
    return this.getAttribute('el')
  }

  set el(value) {
    this.setAttribute('el', value)
  }

  constructor() {
    super()
    this.attachShadow({ mode: 'open' }).innerHTML = template
    updaters.set(this, new Updater(this.shadowRoot.lastElementChild))
  }

  connectedCallback() {
    this.shadowRoot.querySelector('slot').addEventListener('slotchange', () => this.start())
    
    if (this.hasAttribute('data-nav')) {
      this.initializeNavigation()
    }
    
    requestAnimationFrame(() => this.start())
  }

  disconnectedCallback() {
    updaters.get(this).stop()
    if (this.hasAttribute('data-nav') && this.navButtons) {
      this.navButtons.prev.removeEventListener('click', this.handlePrevClick)
      this.navButtons.next.removeEventListener('click', this.handleNextClick)
    }
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.scrollEl = newValue ? this.querySelector(newValue) : null
      this.start()
    }
  }

  initializeNavigation() {
    const customPrev = this.querySelector('[data-nav-prev]')
    const customNext = this.querySelector('[data-nav-next]')
    
    this.navButtons = {
      prev: this.shadowRoot.querySelector('.nav-button.prev'),
      next: this.shadowRoot.querySelector('.nav-button.next')
    }

    if (customPrev) {
      this.navButtons.prev.innerHTML = customPrev.innerHTML
      this.navButtons.prev.classList.add('has-svg')
      customPrev.remove()
    }
    
    if (customNext) {
      this.navButtons.next.innerHTML = customNext.innerHTML
      this.navButtons.next.classList.add('has-svg')
      customNext.remove()
    }

    const prevIcon = this.querySelector('[slot="nav-prev-icon"]')
    const nextIcon = this.querySelector('[slot="nav-next-icon"]')
    
    if (prevIcon) this.navButtons.prev.classList.add('has-svg')
    if (nextIcon) this.navButtons.next.classList.add('has-svg')

    this.handlePrevClick = () => this.navigatePrev()
    this.handleNextClick = () => this.navigateNext()

    this.navButtons.prev.addEventListener('click', this.handlePrevClick)
    this.navButtons.next.addEventListener('click', this.handleNextClick)
  }

  updateNavButtonsState() {
    const el = this.getScrollElement()
    if (!el || !this.navButtons) return

    const isAtStart = el.scrollLeft <= 0
    const isAtEnd = el.scrollLeft >= el.scrollWidth - el.offsetWidth - 1
    

    this.navButtons.prev.classList.toggle('disabled', isAtStart)
    this.navButtons.next.classList.toggle('disabled', isAtEnd)
  }

  getScrollElement() {
    return this.scrollEl || this.firstElementChild
  }

  getVisibleChildren(el) {
    if (!el) return []
    return Array.from(el.children).filter(child => {
      const rect = child.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0
    })
  }

  findClosestChild(el, direction) {
    const children = this.getVisibleChildren(el)
    if (children.length === 0) return null

    const containerRect = el.getBoundingClientRect()
    const scrollLeft = el.scrollLeft

    if (direction === 'next') {
      // Find the first child that's partially or fully outside the right edge
      for (const child of children) {
        const childRect = child.getBoundingClientRect()
        const childOffsetLeft = child.offsetLeft
        
        // Check if child starts beyond the current visible area
        if (childOffsetLeft > scrollLeft + 1) {
          return child
        }
      }
      // If no child found, return the last one
      return children[children.length - 1]
    } else {
      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i]
        const childOffsetLeft = child.offsetLeft
        
        if (childOffsetLeft < scrollLeft - 1) {
          return child
        }
      }
      return children[0]
    }
  }

  navigatePrev() {
    const el = this.getScrollElement()
    if (!el) return

    const targetChild = this.findClosestChild(el, 'prev')
    if (targetChild) {
      el.scrollTo({
        left: targetChild.offsetLeft,
        behavior: 'smooth'
      })
    }
  }

  navigateNext() {
    const el = this.getScrollElement()
    if (!el) return

    const targetChild = this.findClosestChild(el, 'next')
    if (targetChild) {
      el.scrollTo({
        left: targetChild.offsetLeft,
        behavior: 'smooth'
      })
    }
  }

  start() {
    const el = this.scrollEl || this.firstElementChild
    updaters.get(this).start(el, this.scrollEl ? this.firstElementChild : null, this)
    
    if (this.hasAttribute('data-nav') && this.navButtons) {
      const checkState = () => {
        this.updateNavButtonsState()
        if (el && el.scrollWidth === 0) {
          requestAnimationFrame(checkState)
        }
      }
      
      requestAnimationFrame(() => {
        checkState()
        setTimeout(checkState, 100)
      })
    }
  }
}

class Updater {
  constructor(targetElement) {
    const update = this.update.bind(this, targetElement, getComputedStyle(targetElement))
    this.handleScroll = throttle(update)
    this.resizeObserver = new ResizeObserver((entries) => {
      update()
      if (this.hostElement && this.hostElement.hasAttribute('data-nav')) {
        this.hostElement.updateNavButtonsState()
      }
    })
  }

  start(element, rootElement, hostElement) {
    if (this.el) this.stop()
    if (element) {
      element.addEventListener('scroll', this.handleScroll)
      this.resizeObserver.observe(element)
      this.el = element
    }
    if (rootElement) {
      this.resizeObserver.observe(rootElement)
      this.rootEl = rootElement
    }
    if (hostElement) {
      this.hostElement = hostElement
      if (hostElement.hasAttribute('data-nav')) {
        element.addEventListener('scroll', () => hostElement.updateNavButtonsState())
      }
    }
  }

  stop() {
    if (!this.el) return
    this.el.removeEventListener('scroll', this.handleScroll)
    if (this.hostElement && this.hostElement.hasAttribute('data-nav')) {
      this.el.removeEventListener('scroll', () => this.hostElement.updateNavButtonsState())
    }
    this.resizeObserver.disconnect()
    this.el = null
    this.rootEl = null
    this.hostElement = null
  }

  update(targetElement, computedStyle) {
    const { el, rootEl } = this
    if (!el) return

    const maxSize = Number(computedStyle.getPropertyValue('--scroll-shadow-size') || 14)
    const style = {
      '--top': clamp(el.scrollTop, 0, maxSize),
      '--bottom': clamp(el.scrollHeight - el.offsetHeight - el.scrollTop, 0, maxSize),
      '--left': clamp(el.scrollLeft, 0, maxSize),
      '--right': clamp(el.scrollWidth - el.offsetWidth - el.scrollLeft, 0, maxSize),
    }

    if (rootEl) {
      const clientRect = el.getBoundingClientRect()
      const rootClientRect = rootEl.getBoundingClientRect()

      Object.assign(style, {
        top: clamp(clientRect.top - rootClientRect.top),
        bottom: clamp(rootClientRect.bottom - clientRect.bottom),
        left: clamp(clientRect.left - rootClientRect.left),
        right: clamp(rootClientRect.right - clientRect.right),
      })
    }

    for (const key in style) {
      targetElement.style.setProperty(key, `${style[key]}px`)
    }
  }
}

function clamp(num, min = 0, max) {
  if (num < min) return min
  if (num > max) return max
  return num
}

function throttle(callback) {
  let id = null
  return () => {
    if (id) return
    id = requestAnimationFrame(() => {
      callback()
      id = null
    })
  }
}

  if ('customElements' in window && 'ResizeObserver' in window) {
    customElements.define('scroll-shadow', ScrollShadowElement)
  }
})();
